<template>
  <v-row justify="center" class="pa-0 my-0">
    <v-col>
      <v-card flat color="transparent">
        <v-col cols="12">
          <v-row class="mb-3 mt-0">
            <h2
              :style="`color: ${$vuetify.theme.themes.light.primary}`"
              class="pl-2"
            >
              {{ $translate('CHOOSE_DATE') }}
            </h2>
          </v-row>

          <p class="pl-2 pa-sm-0">
            {{ $translate('SELECTED_DATA_EXPLANATION') }}
          </p>

          <!-- Error banner -->
          <v-slide-y-transition>
            <ErrorCard
              v-if="error"
              :errorMessage="errorMessage"
              :functionName="errorToRecallFunction"
              @getLocations="getLocations"
              @getStartDates="getStartDates"
              @getStartBatches="getStartBatches"
              @getEndDates="getEndDates"
              @getEndBatches="getEndBatches"
              @reserveAndAddToCart="reserveAndAddToCart"
            />
          </v-slide-y-transition>

          <v-row v-if="isRental === true" dense class="mb-2 mt-0">
            <v-col v-for="(item, i) in rentalData" :key="i">
              <v-card color="grey lighten-3">
                <v-card-title class="pt-3">{{ item.Name }}</v-card-title>
                <v-card-subtitle class="pb-3">
                  {{ $translate('QUANTITY') }}:
                  {{ item.Quantity }}
                </v-card-subtitle>
              </v-card>
            </v-col>
          </v-row>

          <v-alert
            dense
            type="warning"
            v-if="startDate === '' && rentalData.length > 1"
          >
            {{ $translate('NO_STARTDATES') }}
          </v-alert>

          <v-row justify="space-between" class="my-0">
            <v-col cols="12" class="pa-0">
              <v-col cols="12" md="5" v-if="allLocations.size > 0">
                <v-select
                  :label="$translate('CHOOSE_A_LOCATION')"
                  :items="allLocations"
                  return-object
                  item-text="LocationName"
                  v-model="orderDetails.Location"
                  append-icon="mdi-map-marker"
                  @change="getStartDates()"
                  :disabled="allLocations.length < 2"
                />
              </v-col>
            </v-col>

            <v-col cols="12" md="6">
              <v-menu
                ref="menuStartDate"
                v-model="menuStartDate"
                :close-on-content-click="false"
                :return-value.sync="orderDetails.StartDate"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="computedDateFormattedStartDate"
                    :label="$translate('PLEASE_STARTDATE')"
                    v-bind="attrs"
                    v-on="on"
                    readonly
                    ref="menuStartDate"
                    append-icon="today"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  no-title
                  @change="getStartBatches"
                  event-color="primary"
                  :allowed-dates="allowedStartDates"
                  :events="allowedStartDates"
                  @input="$refs.menuStartDate.save(orderDetails.StartDate)"
                  scrollable
                  first-day-of-week="1"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="orderDetails.StartTime"
                :label="$translate('PLEASE_STARTTIME')"
                :items="listOfStartTimes"
                @change="getEndDates"
                class="my-v-select"
              >
                <template slot="selection" slot-scope="data">
                  <!-- {{ data.item.StartTime }} -  -->
                  <!-- {{ data.item.EndTime }} -->
                  <v-row justify="space-between" align="center" class="ma-0">
                    <v-col cols="6" class="pa-0">
                      {{ getStartTimePresentation(data.item) }}
                    </v-col>
                    <v-col cols="6" class="pa-0">
                      <div class="text-xs-center" align="end">
                        <v-chip
                          :dark="getChipData('darkModus', data.item)"
                          :style="`background-color: ${getChipData(
                            'color',
                            data.item
                          )}`"
                        >
                          {{ $translate(getChipData('text', data.item)) }}
                        </v-chip>
                      </div>
                    </v-col>
                  </v-row>
                </template>
                <template slot="item" slot-scope="data">
                  <template>
                    <v-row justify="space-between" class="py-1">
                      {{ data.item.StartTime + ' - ' + data.item.EndTime }}
                      <div class="text-xs-center">
                        <v-chip
                          :dark="getChipData('darkModus', data.item)"
                          :style="`background-color: ${getChipData(
                            'color',
                            data.item
                          )}`"
                        >
                          {{ $translate(getChipData('text', data.item)) }}
                        </v-chip>
                      </div>
                    </v-row>
                  </template>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu
                ref="menuEndDate"
                v-model="menuEndDate"
                v-if="possibleEndDateList.length > 0"
                :close-on-content-click="false"
                :return-value.sync="orderDetails.EndDate"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="computedDateFormattedEndDate"
                    :label="$translate('PLEASE_ENDDATE')"
                    v-bind="attrs"
                    v-on="on"
                    readonly
                    :disabled="possibleEndDateList.length < 2"
                    append-icon="today"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"
                  no-title
                  event-color="primary"
                  :allowed-dates="allowedEndDate"
                  :events="allowedEndDate"
                  @input="$refs.menuEndDate.save(orderDetails.EndDate)"
                  @change="getEndBatches"
                  scrollable
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="orderDetails.EndTime"
                v-if="listOfEndTimes.length > 0"
                :label="$translate('PLEASE_ENDTIME')"
                :items="listOfEndTimes"
                :disabled="listOfEndTimes.length < 2"
                class="my-v-select"
              >
                <template slot="selection" slot-scope="data">
                  <!-- {{ data.item.StartTime }} -->
                  <!-- - {{ data.item.EndTime }} -->
                  <v-row justify="space-between" align="center" class="ma-0">
                    <v-col cols="6" class="pa-0">
                      {{ getEndTimePresentation(data.item) }}
                    </v-col>
                    <v-col cols="6" class="pa-0">
                      <div class="text-xs-center" align="end">
                        <v-chip
                          :dark="getChipData('darkModus', data.item)"
                          :style="`background-color: ${getChipData(
                            'color',
                            data.item
                          )}`"
                        >
                          {{ $translate(getChipData('text', data.item)) }}
                        </v-chip>
                      </div>
                    </v-col>
                  </v-row>
                </template>
                <template slot="item" slot-scope="data">
                  <template>
                    <v-row justify="space-between" class="py-1">
                      {{ data.item.StartTime + ' - ' + data.item.EndTime }}
                      <div class="text-xs-center">
                        <v-chip
                          :dark="getChipData('darkModus', data.item)"
                          :style="`background-color: ${getChipData(
                            'color',
                            data.item
                          )}`"
                        >
                          {{ $translate(getChipData('text', data.item)) }}
                        </v-chip>
                      </div>
                    </v-row>
                  </template>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12">
              <v-textarea
                :label="$translate('REMARK_OPTIONAL')"
                v-model="remarks"
                rows="3"
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row class="mb-3 mt-0" v-if="Object.keys(listOfTickets).length > 0">
            <h3
              :style="`color: ${$vuetify.theme.themes.light.primary}`"
              class="pl-2 pa-sm-0 mb-2"
            >
              {{ $translate('CHOOSE_AN_OPTION') }}
            </h3>
          </v-row>
          <template v-for="(item, index) in listOfTickets">
            <v-col class="pa-0 mb-1" cols="12" :key="index">
              <v-card elevation="2" class="px-4">
                <v-row class="my-0">
                  <v-col cols="12" md="9">
                    <h3>{{ item.Name }}</h3>
                    <p
                      v-if="item.Price != null && item.Price !== 0"
                      class="text-subtitle-2 mb-1"
                    >
                      {{ $translate('PRICE2') }}: €{{
                        Number(item.Price).toFixed(2)
                      }}
                    </p>
                    <p v-if="item.Price === 0">
                      {{ $translate('FREE') }}
                    </p>
                    <p class="hidden-sm-and-down ma-0">
                      {{ item.Description }}
                    </p>
                  </v-col>
                  <v-col cols="12" md="3" align="center" class="py-0 py-md-1">
                    <v-row justify="start" justify-md="end" class="my-0">
                      <v-btn-toggle group>
                        <v-btn
                          large
                          @click="
                            item.Amount === item.MinAmount
                              ? (item.Amount = item.MinAmount)
                              : item.Amount--
                          "
                          icon
                        >
                          <v-icon color="primary">remove_circle</v-icon>
                        </v-btn>
                        <h2 class="ticketAmount">{{ item.Amount }}</h2>
                        <v-btn
                          large
                          @click="
                            item.Amount === item.MaxAmount
                              ? (item.Amount = item.MaxAmount)
                              : item.Amount++
                          "
                          icon
                        >
                          <v-icon color="primary">add_circle</v-icon>
                        </v-btn>
                      </v-btn-toggle>
                    </v-row>
                  </v-col>
                  <v-col class="hidden-md-and-up" cols="12">
                    <p class="ma-0">{{ item.Description }}</p>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </template>

          <!-- Empty order error -->
          <v-slide-y-transition>
            <v-row class="mt-3 ml-3" v-if="emptyOrderError">
              <span class="error-message">{{
                $translate('EVENT_MESSAGE_SELECT_PRODUCT')
              }}</span>
            </v-row>
          </v-slide-y-transition>

          <!-- Buttons -->
          <v-row class="mt-7">
            <v-btn color="secondary" @click="eventDetailBack()">
              {{ $translate('BACK') }}
            </v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              class="primary-font-color"
              @click="reserveAndAddToCart()"
              :disabled="error"
            >
              {{ addButtonText }}
            </v-btn>
          </v-row>
        </v-col>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import ErrorCard from '@/components/generalComponents/ErrorHandling/ErrorCard';
import doRequest from '@/api/dssRequest';
import utility from '@/shared/utility';

export default {
  name: 'BookingDetails_Events',
  components: {
    ErrorCard,
  },
  props: {
    eventDetails: {
      type: Object,
      required: true,
      default: () => {},
    },
    /**
     * for rental items it is using an array and for a normal item it will be an object
     */
    rentalData: {
      type: [Object, Array],
    },
  },
  created() {},
  beforeDestroy() {},
  data() {
    return {
      // ErrorCard variables
      error: false,
      errorMessage: "Couldn't get available dates...",
      errorToRecallFunction: 'getStartDates',

      orderDetails: {},
      orderType: {},
      allLocations: [],

      listOfStartTimes: [],
      listOfEndTimes: [],
      possibleStartDateList: [],
      possibleEndDateList: [],
      menuStartDate: '',
      menuEndDate: '',
      startDate: '',
      endDate: '',

      selected: 0,

      orderProductItems: {},

      listOfTickets: {},
      products: {},
      user: this.$store.getters['auth/user'],
      remarks: '',
      emptyOrderError: false,
    };
  },
  computed: {
    isRental() {
      if (Array.isArray(this.rentalData) === true) {
        return true;
      }
      return false;
    },
    computedDateFormattedStartDate() {
      if (this.$route.params.event_date){
        return this.$route.params.event_date
      }
      return this.formatDate(this.startDate);
    },
    computedDateFormattedEndDate() {
      if (this.$route.params.event_date){
        return this.$route.params.event_date
      }
      return this.formatDate(this.endDate);
    },
    addButtonText() {
      // if (
      //   this.listOfTickets == null ||
      //   Object.keys(this.listOfTickets).length === 0
      // ) {
      //   return this.$translate('ADD_TO_SHOPPING_CART');
      // }

      // let price = 0;
      // Object.values(this.listOfTickets).forEach((ticket) => {
      //   if (ticket.Amount > 0) {
      //     price += ticket.Price;
      //   }
      // });

      // return price === 0
      //   ? this.$translate('CONTINUE')
      //   : this.$translate('ADD_TO_SHOPPING_CART');

      return this.$translate('EVENT_BOOK');
    },
  },
  mounted() {
    this.getLocations();
  },
  methods: {
    getStartTimePresentation(startTimeObj) {
      if (startTimeObj.StartTime === startTimeObj.EndTime) {
        return startTimeObj.StartTime;
      }
      return startTimeObj.StartTime + ' - ' + startTimeObj.EndTime;
    },
    getEndTimePresentation(endTimeObj) {
      if (endTimeObj.StartTime === endTimeObj.EndTime) {
        return endTimeObj.EndTime;
      }
      return endTimeObj.StartTime + ' - ' + endTimeObj.EndTime;
    },
    getStartBatches() {
      this.error = false;
      const payload = {
        StartDate: this.computedDateFormattedStartDate,
        Products: {},
        OneClickBooking: true,
        LocationId:
          this.eventDetails.Type === 'Rental'
            ? this.eventDetails.LocationId
            : this.orderDetails.Location.LocationId,
      };
      if (this.eventDetails.Type === 'Rental') {
        this.rentalData.forEach((item) => {
          payload.Products[item.Id] = item.Quantity;
        });
      }
      doRequest(
        (response) => {
          this.listOfStartTimes = [];
          this.possibleEndDateList = [];
          this.listOfEndTimes = [];
          if (response.data.StartTimes != null) {
            this.listOfStartTimes = response.data.StartTimes;
            this.orderDetails.StartTime = response.data.DefaultStartTime;
          }
          if (response.data.EndTimes != null) {
            this.listOfEndTimes = response.data.EndTimes;
            this.orderDetails.EndTime = response.data.DefaultEndTime;
          }
          if (response.data.EndDates != null) {
            response.data.EndDates.forEach((date) => {
              this.possibleEndDateList.push(this.formatDate(date));
            });
            this.orderDetails.EndDate = this.formatDate(
              response.data.DefaultEndDate
            );
            this.endDate = this.orderDetails.EndDate;
          }
          if (response.data.Tickets != null) {
            this.listOfTickets = response.data.Tickets;
          }
        },
        (error) => {
          this.error = true;
          this.errorMessage = "Couldn't get start times for this event...";
          this.errorToRecallFunction = 'getStartBatches';
        },
        'getStartBatches',
        this.eventDetails.EventId,
        payload
      );
    },
    getStartDates(item, i) {
      this.selected = i;
      this.error = false;
      this.possibleStartDateList = [];
      const todayDate = utility.getCurrentDateDMY();

      const payload = {
        StartDate: todayDate,
        LocationId:
          this.eventDetails.Type === 'Rental'
            ? this.eventDetails.LocationId
            : this.orderDetails.Location.LocationId,
        Products: {},
        OneClickBooking: true,
      };

      if (this.eventDetails.Type === 'Rental') {
        this.rentalData.forEach((rentalItem) => {
          payload.Products[rentalItem.Id] = rentalItem.Quantity;
        });
      }

      doRequest(
        (response) => {
          response.data.StartDates.forEach((date) => {
            this.possibleStartDateList.push(this.formatDate(date));
          });

          if (response.data.EndDates != null) {
            response.data.EndDates.forEach((date) => {
              this.possibleEndDateList.push(this.formatDate(date));
            });
          }

          if (response.data.StartTimes != null) {
            this.listOfStartTimes = response.data.StartTimes;
          }

          if (response.data.EndTimes != null) {
            this.listOfEndTimes = response.data.EndTimes;
          }

          if (response.data.Tickets != null) {
            this.listOfTickets = response.data.Tickets;
          }

          this.orderDetails.StartDate = this.formatDate(
            response.data.DefaultStartDate
          );
          this.startDate = this.orderDetails.StartDate;

          this.orderDetails.EndDate = this.formatDate(
            response.data.DefaultEndDate
          );
          this.endDate = this.orderDetails.EndDate;

          this.orderDetails.StartTime = response.data.DefaultStartTime;
          this.orderDetails.EndTime = response.data.DefaultEndTime;
        },
        (error) => {
          if(error.value === 0 && error.data.StartDates.length === 0 ){
            this.errorMessage = this.$translate('SOLD_OUT'); //"Tickets are no longer available";
          }
          else{
            this.errorMessage = this.$translate('COULD_NOT_GET_START_DATES'); //"Couldn't get start dates for this event...";
          }
          this.error = true;
          this.errorToRecallFunction = 'getStartDates';
        },
        'getStartDates',
        this.eventDetails.EventId,
        payload
      );
    },
    getEndDates() {
      this.error = false;
      this.possibleEndDateList = [];

      if (this.eventDetails.Type === 'Webshop') {
        if (this.orderType.Type !== 'PickUp') {
          this.orderDetails.OrderType = this.orderType.Type;
        } else {
          this.orderDetails.OrderType = 'TakeAway';
        }
      }

      const payload = {
        PartnerEventId: this.orderType.EventId,
        OrderType: this.orderDetails.OrderType,
        StartDate: this.computedDateFormattedStartDate,
        StartTime: this.orderDetails.StartTime.StartTime,
        LocationId:
          this.eventDetails.Type === 'Rental'
            ? this.eventDetails.LocationId
            : this.orderDetails.Location.LocationId,
        Products: {},
        OneClickBooking: true,
      };

      if (this.eventDetails.Type === 'Rental') {
        this.rentalData.forEach((item) => {
          payload.Products[item.Id] = item.Quantity;
        });
      }

      doRequest(
        (response) => {
          this.possibleEndDateList = [];

          if (response.data.EndDates != null) {
            response.data.EndDates.forEach((date) => {
              this.possibleEndDateList.push(this.formatDate(date));
            });
            this.listOfEndTimes = response.data.EndTimes;
            this.listOfTickets = response.data.Tickets;
            this.orderDetails.EndDate = this.formatDate(
              response.data.DefaultEndDate
            );
            this.endDate = this.orderDetails.EndDate;
            this.orderDetails.EndTime = response.data.DefaultEndTime;
          }
        },
        () => {
          this.error = true;
          this.errorMessage = "Couldn't get end dates for this event...";
          this.errorToRecallFunction = 'getEndDates';
        },
        'getEndDates',
        this.eventDetails.EventId,
        payload
      );
    },
    getEndBatches() {
      this.error = false;
      if (this.eventDetails.Type === 'Webshop') {
        if (this.orderType.Type !== 'PickUp') {
          this.orderDetails.OrderType = this.orderType.Type;
        } else {
          this.orderDetails.OrderType = 'TakeAway';
        }
      }

      const payload = {
        PartnerEventId: this.orderType.EventId,
        OrderType: this.orderDetails.OrderType,
        StartDate: this.computedDateFormattedStartDate,
        StartTime: this.orderDetails.StartTime.StartTime,
        LocationId:
          this.eventDetails.Type === 'Rental'
            ? this.eventDetails.LocationId
            : this.orderDetails.Location.LocationId,
        Products: {},
        OneClickBooking: true,
        EndDate: this.formatDate(this.endDate),
      };

      if (this.eventDetails.Type === 'Rental') {
        payload.Products = {
          [this.rentalData.Id]: Number(this.rentalData.Quantity),
        };
      }

      doRequest(
        (response) => {
          this.listOfEndTimes = response.data.EndTimes;
          this.listOfTickets = response.data.Tickets;
          this.orderDetails.EndTime = response.data.DefaultEndTime;
        },
        () => {
          this.error = true;
          this.errorMessage = "Couldn't get end dates for this event...";
          this.errorToRecallFunction = 'getEndBatches';
        },
        'getEndBatches',
        this.eventDetails.EventId,
        payload
      );
    },
    getLocations() {
      this.error = false;
      const payload = {
        Densities: [0, 1, 2, 3, 4],
      };

      doRequest(
        (response) => {
          this.allLocations = response.data.Locations;
          [this.orderDetails.Location] = this.allLocations;
          this.getStartDates();
        },
        () => {
          this.error = true;
          this.errorMessage = "Couldn't find locations for this event...";
          this.errorToRecallFunction = 'getLocations';
        },
        'getLocations',
        this.eventDetails.EventId,
        payload
      );
    },
    reserveAndAddToCart() {
      if (this.eventDetails.specialRequests != null) {
        if (
          this.eventDetails.specialRequests.length <= 0 &&
          (this.eventDetails.shoppingCart == null ||
            Object.keys(this.eventDetails.shoppingCart).length === 0)
        ) {
          window.EventBus.fire('setCurrentComponent', { component: 'Details' });
          return;
        }
      } else if (
        this.eventDetails.shoppingCart != null &&
        Object.keys(this.eventDetails.shoppingCart).length === 0
      ) {
        window.EventBus.fire('setCurrentComponent', { component: 'Details' });
        return;
      }

      if (this.eventDetails.Type === 'Webshop') {
        if (this.orderType.Type !== 'PickUp') {
          this.orderDetails.OrderType = this.orderType.Type;
        } else {
          this.orderDetails.OrderType = 'TakeAway';
        }
      }

      const order = {
        Note: this.remarks,
        LocationId:
          this.eventDetails.Type === 'Webshop'
            ? ''
            : this.orderDetails.Location.LocationId,
        PriceCategories: this.listOfTickets,
        StartDate: this.computedDateFormattedStartDate,
        StartTime: this.orderDetails.StartTime.StartTime,
        EndDate: this.computedDateFormattedEndDate,
        StartBatchGroupId: this.orderDetails.StartTime.BatchGroupId,
      };

      if (this.orderDetails.EndTime) {
        order.EndTime = this.orderDetails.EndTime.EndTime;
        order.EndBatchGroupId = this.orderDetails.EndTime.BatchGroupId;
      } else {
        order.EndTime = '';
        order.EndBatchGroupId = 0;
      }

      if (this.eventDetails.Type === 'Rental') {
        order.LocationId = this.eventDetails.LocationId;
        order.EventType = this.eventDetails.Type;
        this.rentalData.forEach((item) => {
          this.orderProductItems[item.Id] = {
            [item.ItemId]: Number(item.Quantity),
          };
        });
        order.Products = this.orderProductItems;
        console.log('order', this.orderProductItems);
      }

      order.EventId = this.eventDetails.EventId;
      window.EventBus.fire('reserve', order);
    },
    allowedStartDates(val) {
      return this.possibleStartDateList.indexOf(val) !== -1;
    },
    allowedEndDate(val) {
      return this.possibleEndDateList.indexOf(val) !== -1;
    },
    getChipData(sendedInfo, item) {
      switch (sendedInfo) {
        case 'text':
          if (item.Occupancy < 0.25) return 'NOT_BUSY'; // green
          if (item.Occupancy < 0.5) return 'NORMAL'; // yellow
          return 'ALMOST_FULL'; // red
        case 'color':
          if (item.State === 0) return 'rgba(25, 181, 254, 1)'; // blue
          if (item.Occupancy < 0.25) return 'rgba(67, 160, 71, 1)'; // green
          if (item.Occupancy < 0.5) return 'rgba(240, 255, 0, 0.8)'; // yellow
          return 'rgba(240, 52, 52, 0.8)'; // red
        case 'darkModus':
          if (item.Occupancy < 0.25) return true;
          if (item.Occupancy < 0.5) return false;
          return true;
        default:
          return false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      return `${day}-${month}-${year}`;
    },
    eventDetailBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.ticketAmount {
  align-self: center;
}
.error-message {
  color: red;
}
.my-v-select::v-deep .v-select__selections input {
  display: none;
}
</style>
